const DRIVER_MSG_PREFIX = "DriverMsg_";
const HEADER_KEY = "notifIcon";
const LAST_SEEN_PREFIX = "DriverMdtLastSeen_";
const LAST_SOUND_PREFIX = "DriverMdtLastSound_";

function cleanIdentifier(value) {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value).trim();
}

export function normalizeVehNo(value) {
  const clean = cleanIdentifier(value);
  return clean.toLowerCase() === "broadcast" ? "xxxx" : clean;
}

function cleanAffiliate(value) {
  return cleanIdentifier(value || "unknown");
}

function makeDriverMessageKey(affiliateID, identifier) {
  const cleanAff = cleanAffiliate(affiliateID);
  return `${DRIVER_MSG_PREFIX}${cleanAff}_${identifier}`;
}

function getDriverMessageKeys({ affiliateID = "", vehNo = "", driverNo = "" } = {}, forWrite = false) {
  const normVeh = normalizeVehNo(vehNo);
  const identifiers = [normVeh, cleanIdentifier(driverNo)].filter(Boolean);
  
  // To clear legacy keys that were saved before the broadcast normalizer fix
  if (!forWrite && normVeh === "xxxx") {
    identifiers.push("broadcast");
  }

  const cleanAff = cleanAffiliate(affiliateID);
  
  const keys = [];
  identifiers.forEach((value) => {
    keys.push(makeDriverMessageKey(cleanAff, value));
    // Fallback to unknown only for reads/clears, to avoid polluting other affiliates on write
    if (!forWrite && cleanAff !== "unknown") {
      keys.push(makeDriverMessageKey("unknown", value));
    }
  });

  return [...new Set(keys)];
}

function toNotificationPayload({ affiliateID = "", affiliateName = "", vehNo = "", driverNo = "", messageID = "" } = {}) {
  return JSON.stringify({
    affiliateID: cleanIdentifier(affiliateID),
    affiliateName: cleanIdentifier(affiliateName),
    vehNo: normalizeVehNo(vehNo),
    driverNo: cleanIdentifier(driverNo),
    messageID: cleanIdentifier(messageID),
    createdAt: Date.now()
  });
}

function parseNotificationPayload(rawValue) {
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (err) {
    console.warn("[messagingNotificationState] Could not parse stored payload, treating as legacy:", rawValue);
    return { legacy: true };
  }
}

const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Removes any DriverMsg_* localStorage entries older than MAX_AGE_MS.
 * Called on every write to prevent unbounded key accumulation.
 */
function cleanExpiredEntries() {
  try {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(DRIVER_MSG_PREFIX)) {
        const payload = parseNotificationPayload(localStorage.getItem(key));
        if (!payload || !payload.createdAt || (now - payload.createdAt) > MAX_AGE_MS) {
          localStorage.removeItem(key);
        }
      }
    }
  } catch (err) {
    // Silently ignore — cleanup is best-effort
  }
}

function dispatchStateChangeEvent() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("messagingNotificationStateChange"));
  }
}

export function setMessagingHeaderNotification(isActive) {
  localStorage.setItem(HEADER_KEY, String(Boolean(isActive)));
  dispatchStateChangeEvent();
}

export function setPendingDriverMessage(details = {}) {
  cleanExpiredEntries();
  const keys = getDriverMessageKeys(details, true);
  const payload = toNotificationPayload(details);

  keys
    .filter((key) => key.includes("_"))
    .forEach((key) => {
    localStorage.setItem(key, payload);
  });
  setMessagingHeaderNotification(hasAnyPendingDriverMessage());
}

export function hasPendingDriverMessage(details = {}) {
  const { affiliateID = "", vehNo = "", driverNo = "" } = details;
  const normalizedAffiliateID = cleanIdentifier(affiliateID);
  const normalizedVehNo = normalizeVehNo(vehNo);
  const normalizedDriverNo = cleanIdentifier(driverNo);
  const keys = getDriverMessageKeys(details, false);

  return keys.some((key) => {
    const rawValue = localStorage.getItem(key);
    if (!rawValue) {
      return false;
    }

    const payload = parseNotificationPayload(rawValue);
    if (!payload) {
      return false;
    }

    if (payload.legacy) {
      return true;
    }

    const payloadVehNo = normalizeVehNo(payload.vehNo);
    const payloadDriverNo = cleanIdentifier(payload.driverNo);
    const payloadAffiliateID = cleanIdentifier(payload.affiliateID);

    if (
      normalizedAffiliateID !== "" &&
      payloadAffiliateID !== "" &&
      payloadAffiliateID !== "unknown" &&
      payloadAffiliateID !== normalizedAffiliateID
    ) {
      return false;
    }

    return (
      (normalizedVehNo !== "" && payloadVehNo === normalizedVehNo) ||
      (normalizedDriverNo !== "" && payloadDriverNo === normalizedDriverNo)
    );
  });
}

export function clearPendingDriverMessage(details = {}) {
  const keys = getDriverMessageKeys(details, false);
  keys.forEach((key) => {
    localStorage.removeItem(key);
  });

  const { affiliateID = "", vehNo = "" } = details;
  const normalizedAffiliateID = cleanIdentifier(affiliateID);
  const normalizedVehNo = normalizeVehNo(vehNo);

  // Drain matching alerts from unmatched alerts queue
  try {
    const unmatched = getUnmatchedAlerts();
    const remaining = unmatched.filter((alert) => {
      const alertVeh = normalizeVehNo(alert.VehicleNo || alert.vehicleNo);
      const alertAff = cleanIdentifier(alert.AffiliateID || alert.affiliateID);
      const isMatchVeh = normalizedVehNo !== "" && alertVeh === normalizedVehNo;
      const isMatchAff = 
        normalizedAffiliateID === "" || 
        normalizedAffiliateID === "all" || 
        normalizedAffiliateID === "-1" ||
        alertAff === "" || 
        alertAff === "all" ||
        alertAff === "-1" ||
        alertAff === normalizedAffiliateID;
      return !(isMatchVeh && isMatchAff);
    });

    if (remaining.length !== unmatched.length) {
      saveUnmatchedAlerts(remaining);
    }
    setMessagingHeaderNotification(hasAnyPendingDriverMessage() || remaining.length > 0);
  } catch (err) {
    setMessagingHeaderNotification(hasAnyPendingDriverMessage());
  }

  dispatchStateChangeEvent();
}

export function getPendingDriverMessages() {
  cleanExpiredEntries();
  const messages = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(DRIVER_MSG_PREFIX)) {
        const suffix = key.slice(DRIVER_MSG_PREFIX.length);
        // Scoped key is structured as DriverMsg_{affiliateID}_{identifier}.
        // Thus, suffix must contain an underscore (e.g. "12_8001" or "unknown_8001").
        // We ignore legacy unscoped keys (e.g. "8001") which do not contain an underscore.
        if (suffix.includes("_")) {
          const payload = parseNotificationPayload(localStorage.getItem(key));
          if (payload && !payload.legacy) {
            messages.push(payload);
          }
        }
      }
    }
  } catch (err) {
    return [];
  }

  const unique = new Map();
  messages.forEach((message) => {
    const uniqueKey = [
      cleanIdentifier(message.affiliateID),
      normalizeVehNo(message.vehNo),
      cleanIdentifier(message.driverNo),
      cleanIdentifier(message.messageID)
    ].join("|");

    if (!unique.has(uniqueKey)) {
      unique.set(uniqueKey, message);
    }
  });

  return Array.from(unique.values());
}

export function hasAnyPendingDriverMessage() {
  return getPendingDriverMessages().length > 0;
}

export function getPendingDriverMessageCount() {
  return getPendingDriverMessages().length;
}

function getUserScopedKey(prefix, userID = "") {
  return `${prefix}${cleanIdentifier(userID || "default")}`;
}

export function getLastSeenDriverMessageID(userID = "") {
  const parsed = parseInt(localStorage.getItem(getUserScopedKey(LAST_SEEN_PREFIX, userID)), 10);
  return Number.isInteger(parsed) ? parsed : -1;
}

export function setLastSeenDriverMessageID(userID = "", messageID = -1) {
  const parsed = parseInt(messageID, 10);
  if (Number.isInteger(parsed) && parsed > -1) {
    localStorage.setItem(getUserScopedKey(LAST_SEEN_PREFIX, userID), String(parsed));
  }
}

export function getLastSoundedDriverMessageID(userID = "") {
  const parsed = parseInt(localStorage.getItem(getUserScopedKey(LAST_SOUND_PREFIX, userID)), 10);
  return Number.isInteger(parsed) ? parsed : -1;
}

export function setLastSoundedDriverMessageID(userID = "", messageID = -1) {
  const parsed = parseInt(messageID, 10);
  if (Number.isInteger(parsed) && parsed > -1) {
    localStorage.setItem(getUserScopedKey(LAST_SOUND_PREFIX, userID), String(parsed));
  }
}

const UNMATCHED_ALERTS_KEY = "unmatchedAlertsQueue";

export function getUnmatchedAlerts() {
  try {
    const raw = localStorage.getItem(UNMATCHED_ALERTS_KEY);
    if (!raw) {
      return [];
    }
    const alerts = JSON.parse(raw);
    const now = Date.now();
    // Expire unmatched alerts older than 24 hours (MAX_AGE_MS)
    const active = alerts.filter(alert => !alert.receivedAt || (now - alert.receivedAt) < MAX_AGE_MS);
    if (active.length !== alerts.length) {
      saveUnmatchedAlerts(active);
    }
    return active;
  } catch (err) {
    return [];
  }
}

export function saveUnmatchedAlerts(alerts) {
  try {
    localStorage.setItem(UNMATCHED_ALERTS_KEY, JSON.stringify(alerts));
  } catch (err) {
    console.error("[messagingNotificationState] Could not save unmatched alerts:", err);
  }
}

export function addUnmatchedAlerts(newAlerts) {
  const existing = getUnmatchedAlerts();
  const merged = [...existing];
  const now = Date.now();
  newAlerts.forEach(newAlert => {
    const isDup = merged.some(item =>
      String(item.VehicleNo) === String(newAlert.VehicleNo) &&
      String(item.AffiliateID) === String(newAlert.AffiliateID) &&
      String(item.MessageID) === String(newAlert.MessageID)
    );
    if (!isDup) {
      merged.push({ ...newAlert, receivedAt: now });
    }
  });
  saveUnmatchedAlerts(merged);
}

